import { Button, Group, Popover, rem, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { izmeneAcc } from '@reditelji/shared';
import { useState } from 'react';
import { useShiftMutations } from '../api/hooks';
import { useSession } from '../api/session';
import { useVolunteerMutations, type CancelRecord, type VolunteerRow } from '../api/volunteers';
import { useDeepLinks } from '../lib/deepLinks';
import { range } from '../timeline/derive';
import {
  comeBackDone,
  comeBackHint,
  dayHeading,
  deletedSince,
  freedStanding,
  recordedAt,
} from './cancelCopy';
import { CARD, MONO, SECTION_LABEL } from './ui';
import { bySuffix, useCoordinatorLabel } from '../lib/people';

const RED = 'oklch(0.5 0.13 25)';

const STANDING_TONE: Record<ReturnType<typeof freedStanding>['kind'], React.CSSProperties> = {
  free: { fontFamily: MONO, fontSize: rem(11.5), fontWeight: 700, color: RED },
  taken: { fontSize: rem(12), color: 'oklch(0.36 0.06 155)' },
  past: { fontSize: rem(11.5), color: 'oklch(0.55 0.012 90)' },
  gone: { fontSize: rem(11.5), color: 'oklch(0.55 0.012 90)' },
};

/**
 * In place of "Pot do razporeditve" once somebody is not coming (SPEC §1.1K):
 * who marked it and when, their reason, and where each freed shift stands now.
 * The record is read live, so a slot reads "zdaj: Tina Golob" as soon as
 * somebody takes it.
 */
export function CancelledCard({
  volunteer: v,
  record,
}: {
  volunteer: VolunteerRow;
  record: CancelRecord | null;
}) {
  const labelOf = useCoordinatorLabel();
  const { goGap } = useDeepLinks();
  const shiftMut = useShiftMutations();
  const { can } = useSession();
  const canEdit = can('people:write');
  const canPlan = can('plan:write');
  const deleted = record ? record.freedTotal - record.freed.length : 0;
  return (
    <div
      style={{
        ...CARD,
        border: '1px solid oklch(0.86 0.06 25)',
        background: 'oklch(0.993 0.008 25)',
        padding: '14px 16px 15px',
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
        <span style={{ ...SECTION_LABEL, color: RED, fontWeight: 700, whiteSpace: 'nowrap' }}>NE PRIDE</span>
        <span style={{ fontSize: rem(12.5), color: 'oklch(0.55 0.012 90)', flex: 1, minWidth: 0 }}>
          zabeleženo {recordedAt(v.cancelledAt)}
          {bySuffix(labelOf(v.cancelledById, v.cancelledByName, ''))}
        </span>
        {record && canEdit ? <ComeBackButton volunteer={v} record={record} /> : null}
      </div>
      <div
        style={{
          margin: '9px 0 4px',
          padding: '7px 10px',
          borderLeft: '2px solid oklch(0.88 0.05 25)',
          borderRadius: '0 7px 7px 0',
          background: 'oklch(1 0 0)',
          fontSize: rem(13),
          lineHeight: 1.45,
          fontStyle: v.cancelReason ? 'italic' : 'normal',
          color: v.cancelReason ? 'oklch(0.35 0.012 90)' : 'oklch(0.6 0.012 90)',
        }}
      >
        {v.cancelReason ? `„${v.cancelReason}“` : 'brez razloga'}
      </div>

      {!record ? (
        <Text size="sm" c="dimmed" mt={8}>
          Nalagam izmene …
        </Text>
      ) : (
        <>
          <SubHead>SPROŠČENE IZMENE</SubHead>
          {record.freedTotal === 0 ? (
            <Text size="sm" c="dimmed">
              Nobena izmena ni bila sproščena.
            </Text>
          ) : null}
          <Stack gap={5}>
            {record.freed.map((f) => {
              const st = freedStanding(f, record.returnable);
              return (
                <Line
                  key={f.shiftId}
                  code={f.postCode}
                  micro={f.postMicro}
                  when={`${dayHeading(f.dayLabel, f.date)} · ${range(f.fromMin, f.toMin)}`}
                >
                  <span style={STANDING_TONE[st.kind]}>{st.text}</span>
                  {st.kind === 'free' && canPlan ? (
                    <Button
                      size="compact-xs"
                      variant="default"
                      onClick={() =>
                        goGap({
                          eventDayId: f.eventDayId,
                          postId: f.postId,
                          fromMin: f.fromMin,
                          toMin: f.toMin,
                          shiftId: f.shiftId,
                        })
                      }
                    >
                      Poišči zamenjavo
                    </Button>
                  ) : null}
                </Line>
              );
            })}
          </Stack>
          {deleted > 0 ? (
            <Text size="xs" c="dimmed" mt={6}>
              {deletedSince(deleted)}
            </Text>
          ) : null}
          {record.kept.length || record.stillFree.length ? (
            <>
              <SubHead>OSTALE PRI OSEBI</SubHead>
              <Stack gap={5}>
                {record.kept.map((k) => (
                  <Line
                    key={k.shiftId}
                    code={k.postCode}
                    micro={k.postMicro}
                    when={`${dayHeading(k.dayLabel, k.date)} · ${range(k.fromMin, k.toMin)}`}
                  >
                    <span style={STANDING_TONE.past}>{k.label}</span>
                  </Line>
                ))}
                {/* Kept at the mark, free to go now — a check-out recorded
                    since ends what kept it. Still theirs until freed by hand. */}
                {record.stillFree.map((f) => (
                  <Line
                    key={f.shiftId}
                    code={f.postCode}
                    micro={f.postMicro}
                    when={`${dayHeading(f.dayLabel, f.date)} · ${range(f.fromMin, f.toMin)}`}
                  >
                    <span style={{ ...STANDING_TONE.free, fontFamily: 'inherit', fontWeight: 600 }}>
                      {f.running ? 'teče brez osebe' : 'nič je ne drži več'}
                    </span>
                    {canPlan ? (
                      <Button
                        size="compact-xs"
                        variant="default"
                        loading={shiftMut.update.isPending}
                        onClick={() =>
                          shiftMut.update.mutate({
                            id: f.shiftId,
                            updatedAt: f.updatedAt,
                            volunteerId: null,
                            state: 'open',
                          })
                        }
                      >
                        Sprosti
                      </Button>
                    ) : null}
                  </Line>
                ))}
              </Stack>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

function SubHead({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: rem(11),
        fontWeight: 800,
        letterSpacing: '0.06em',
        color: 'oklch(0.55 0.012 90)',
        margin: '12px 0 6px',
      }}
    >
      {children}
    </div>
  );
}

function Line({
  code,
  micro,
  when,
  children,
}: {
  code: string;
  micro: string;
  when: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 9px',
        borderRadius: 8,
        border: '1px solid oklch(0.94 0.005 95)',
        background: 'oklch(1 0 0)',
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: rem(12.5), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <b style={{ fontFamily: MONO, marginRight: 5 }}>{code}</b>
          {micro}
        </div>
        <div style={{ fontFamily: MONO, fontSize: rem(11.5), color: 'oklch(0.58 0.012 90)' }}>{when}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
        {children}
      </div>
    </div>
  );
}

/**
 * "Vendarle pride…" — hands back the freed shifts that are still open
 * (SPEC §1.1K). What the confirmation does is said before it is pressed: all
 * of them back keeps it as it was, only some puts the schedule into
 * re-confirmation.
 */
export function ComeBackButton({ volunteer: v, record }: { volunteer: VolunteerRow; record: CancelRecord }) {
  const [opened, setOpened] = useState(false);
  const mut = useVolunteerMutations();
  const back = record.freed.filter((f) => record.returnable.includes(f.shiftId));
  const notBack = record.freed.filter((f) => !record.returnable.includes(f.shiftId));
  const run = (restore: string[]) =>
    mut.uncancel.mutate(
      { id: v.id, restore },
      {
        onSuccess: (res) => {
          setOpened(false);
          notifications.show({ color: 'green', message: comeBackDone(res) });
        },
      },
    );

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" width={360} withArrow shadow="md">
      <Popover.Target>
        <button
          type="button"
          onClick={() => setOpened((o) => !o)}
          style={{
            flexShrink: 0,
            cursor: 'pointer',
            padding: '5px 11px',
            borderRadius: 7,
            border: '1px solid oklch(0.9 0.006 95)',
            background: 'oklch(1 0 0)',
            fontSize: rem(12.5),
            fontWeight: 500,
            fontFamily: 'inherit',
            color: 'oklch(0.42 0.012 90)',
            whiteSpace: 'nowrap',
          }}
        >
          Vendarle pride…
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={8}>
          <Text size="sm">
            <b>{v.name}</b> vendarle pride.
          </Text>
          {back.length ? (
            <div>
              <Text size="xs" c="dimmed" mb={2}>
                Še proste:
              </Text>
              <Stack gap={2}>
                {back.map((f) => (
                  <Text size="sm" key={f.shiftId}>
                    <b style={{ fontFamily: MONO, marginRight: 5 }}>{f.postCode}</b>
                    {dayHeading(f.dayLabel, f.date)} {range(f.fromMin, f.toMin)}
                  </Text>
                ))}
              </Stack>
            </div>
          ) : null}
          {notBack.map((f) => (
            <Text size="xs" c="dimmed" key={f.shiftId}>
              {f.postCode} — {freedStanding(f, record.returnable).text}
            </Text>
          ))}
          <Text
            size="xs"
            style={{
              background: 'oklch(0.965 0.005 95)',
              borderRadius: 6,
              padding: '6px 8px',
              color: 'oklch(0.45 0.012 90)',
            }}
          >
            {comeBackHint(record)}
          </Text>
          <Group justify="flex-end" gap={6}>
            {back.length ? (
              <Button
                size="compact-sm"
                style={{ background: 'oklch(0.35 0.05 245)' }}
                loading={mut.uncancel.isPending}
                onClick={() => run(back.map((f) => f.shiftId))}
              >
                Vrni in dodeli nazaj {izmeneAcc(back.length)}
              </Button>
            ) : null}
            <Button
              size="compact-sm"
              variant="default"
              disabled={mut.uncancel.isPending}
              title={
                back.length
                  ? 'Oseba se vrne brez izmen in razpored čaka potrditev; sproščene ostanejo proste.'
                  : undefined
              }
              onClick={() => run([])}
            >
              {back.length ? 'Samo vrni' : 'Vrni'}
            </Button>
            <Button size="compact-sm" variant="subtle" color="gray" onClick={() => setOpened(false)}>
              Prekliči
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
